import { DateFormat, DateTimeFormat, TimeFormat } from '$provider/constants/Date';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const isUtcDateString = (dateString: string): boolean => {
  const regex = new RegExp(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}Z$/, 'g');
  return regex.test(dateString);
};

export const isISODateString = (dateString: string): boolean => {
  const regex = new RegExp(
    /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})[+-](\d{2}):(\d{2})/,
    'g'
  );
  return regex.test(dateString);
};

export const isNonUTCDateString = (dateString: string): boolean => {
  // To check for date string that is not UTC or ISO (eg: 2009-02-11T00:00:00.0000000)
  const regex = new RegExp(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/, 'g');
  return regex.test(dateString);
};

export const dateStringtoET = (dateString: string, milliseconds = 0): string => {
  if (!dateString) {
    return '';
  }
  const formatedDateTime = dayjs
    .utc(dateString)
    .add(milliseconds, 'ms')
    .tz('America/New_York')
    .format(`${DateFormat.Default}, ${DateTimeFormat.SimpleTimeFormat}`);
  return `${formatedDateTime} (ET)`;
};

export const getFormatedDateString = (dateString: string, dateFormat: string): string => {
  if (!dateString || !dateFormat) {
    return '';
  }
  return dayjs(dateString).format(dateFormat);
};

export const addMillisecondsToLocalDateString = (dateString: string, milliseconds = 0): string => {
  return dayjs(dateString).add(milliseconds, 'ms').format(DateFormat.Default);
};

export const addMilliseconds = (dateString: string, milliseconds = 0): string => {
  if (!dateString) {
    return '';
  }
  return dayjs(dateString).add(milliseconds, 'ms').toISOString();
};

export const getLocalDateTimeString = (dateString: string, milliseconds = 0): string => {
  if (!dateString) {
    return '';
  }
  const formatedDate = dayjs.utc(dateString).add(milliseconds, 'ms').format();
  return dayjs(formatedDate).format(`${DateFormat.Default}, ${DateTimeFormat.SimpleTimeFormat}`);
};

export const getDateDiff = (
  toDate: string,
  fromDate = dayjs().format(DateFormat.Default)
): number => {
  const currentDate = dayjs(fromDate);

  const actualDate = dayjs(toDate);
  return actualDate.diff(currentDate, 'days');
};

export const convertToTimeFormat = (timeString: string): string => {
  if (!timeString) {
    return '';
  }
  // To check for valid time string (eg: 16:30:00 )
  const regex = new RegExp(/(\d{2}):(\d{2}):(\d{2})/, 'g');
  if (!regex.test(timeString)) {
    return timeString;
  }
  return dayjs(timeString, 'h:mm:ss').format(DateTimeFormat.SimpleTimeFormat);
};

export const getUtcDateTimeString = (dateString: string): string => {
  if (!dateString) {
    return '';
  }

  return dayjs(dateString).utc().format();
};

export const getCurrentTimeString = (): string => {
  return dayjs().format(TimeFormat.Default);
};

export const formatUtcTimestampWithTimezone = (dateString: string | null): string => {
  if (!dateString) {
    return '';
  }
  return dayjs(dateString).utc().format('MMM D, YYYY h:mm:ss A [UTC]');
};

export const formatMillisecondsToReadableTime = (ms: number): string => {
  const d = dayjs.duration(ms);

  const totalHours = Math.floor(d.asHours());
  const minutes = d.minutes();
  const seconds = d.seconds();

  if (totalHours > 0) return `${totalHours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};

export const formatSessionDate = (date: Date | string | undefined): string => {
  return getLocalDateTimeString(date || new Date()).replace(', ', ' • ');
};
